const CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_COUNTER_KEY =
  "tint-platform-clerk-self-service-signup-attempt-counter"
const CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_KEY = "tint-platform-clerk-self-service-signup-attempt"
const CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY = "tint-platform-clerk-self-service-signup-intent"
const CLERK_SELF_SERVICE_SIGNUP_INTENT_TTL_MS = 24 * 60 * 60 * 1_000
const DEPRECATED_LEGACY_AUTH_KEYS = [
  "authMode",
  "accessToken",
  "refreshToken",
  "lastLogin",
  "lastPassword",
  "rememberMe",
] as const

export const beginClerkSelfServiceSignupAttempt = (): void => {
  try {
    const previousAttempt = Number.parseInt(
      sessionStorage.getItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_COUNTER_KEY) ?? "0",
      10
    )
    const attemptId = String(Number.isFinite(previousAttempt) ? previousAttempt + 1 : 1)
    sessionStorage.setItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_COUNTER_KEY, attemptId)
    sessionStorage.setItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_KEY, attemptId)
  } catch {
    // OAuth still works when storage is blocked; actor resolution fails closed.
  }
}

export const clearClerkSelfServiceSignupAttempt = (): void => {
  try {
    sessionStorage.removeItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_KEY)
  } catch {
    // Ignore storage access errors (private mode, disabled storage, etc.).
  }
}

export const completeClerkSelfServiceSignupAttempt = (input: {
  isTransferable: boolean
  now?: number
  status: string | null | undefined
  subject: string
}): boolean => {
  try {
    const attemptId = sessionStorage.getItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_KEY) ?? ""
    const isCompletedNewSignup =
      Boolean(attemptId) &&
      Boolean(input.subject.trim()) &&
      input.status === "complete" &&
      !input.isTransferable

    sessionStorage.removeItem(CLERK_SELF_SERVICE_SIGNUP_ATTEMPT_KEY)
    if (!isCompletedNewSignup) return false

    const now = input.now ?? Date.now()
    sessionStorage.setItem(
      CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY,
      JSON.stringify({
        attemptId,
        expiresAt: now + CLERK_SELF_SERVICE_SIGNUP_INTENT_TTL_MS,
        subject: input.subject.trim(),
      })
    )
    return true
  } catch {
    return false
  }
}

/**
 * A Google sign-in for a not-yet-existing account is transferred by Clerk into
 * a sign-up that creates the account mid-callback. No attempt marker exists
 * for it (the sign-in path never begins one), yet the account is brand new and
 * must be provisioned like any self-service signup — otherwise it can never
 * resolve a workspace on any future sign-in.
 */
export const recordTransferredSignupIntent = (subject: string, now = Date.now()): boolean => {
  const trimmedSubject = subject.trim()
  if (!trimmedSubject) return false

  try {
    sessionStorage.setItem(
      CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY,
      JSON.stringify({
        attemptId: "sign-in-transfer",
        expiresAt: now + CLERK_SELF_SERVICE_SIGNUP_INTENT_TTL_MS,
        subject: trimmedSubject,
      })
    )
    return true
  } catch {
    return false
  }
}

export const readClerkSelfServiceSignupIntent = (
  subject: string,
  now = Date.now()
): string | null => {
  try {
    const serializedIntent = sessionStorage.getItem(CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY)
    if (!serializedIntent) return null

    const intent = JSON.parse(serializedIntent) as {
      attemptId?: unknown
      expiresAt?: unknown
      subject?: unknown
    }
    if (
      typeof intent.attemptId !== "string" ||
      !intent.attemptId ||
      typeof intent.expiresAt !== "number" ||
      !Number.isFinite(intent.expiresAt) ||
      typeof intent.subject !== "string" ||
      !intent.subject
    ) {
      sessionStorage.removeItem(CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY)
      return null
    }

    if (intent.expiresAt <= now) {
      sessionStorage.removeItem(CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY)
      return null
    }
    if (intent.subject !== subject.trim()) return null

    return intent.attemptId
  } catch {
    return null
  }
}

export const acknowledgeClerkSelfServiceSignupIntent = (subject: string): void => {
  try {
    if (readClerkSelfServiceSignupIntent(subject))
      sessionStorage.removeItem(CLERK_SELF_SERVICE_SIGNUP_INTENT_KEY)
  } catch {
    // Ignore storage access errors (private mode, disabled storage, etc.).
  }
}

export const clearDeprecatedLegacyAuthKeys = (): void => {
  try {
    for (const key of DEPRECATED_LEGACY_AUTH_KEYS) localStorage.removeItem(key)
  } catch {
    // Ignore storage access errors (private mode, disabled storage, etc.).
  }
}
